import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Tags, FolderKanban, Wrench } from "lucide-react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Split a record's amount across cost tags (projects / cost centres).
export function AllocationDialog({ row, apiPath, open, onOpenChange, onSaved }) {
  const [tags, setTags] = useState([]);
  const [lines, setLines] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const base = Math.abs(Number(row?.amount || 0));

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .get("/cost-tags")
      .then(({ data }) => setTags(data.tags || []))
      .catch(() => toast.error("Could not load cost tags"))
      .finally(() => setLoading(false));
    const existing = (row?.allocations || []).map((a) => ({ tag_id: a.tag_id, pct: a.pct }));
    setLines(existing.length ? existing : [{ tag_id: "", pct: 100 }]);
  }, [open, row]);

  const allocatedPct = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.pct) || 0), 0),
    [lines]
  );
  const untaggedPct = Math.max(0, 100 - allocatedPct);
  const over = allocatedPct > 100.01;

  const addLine = () => setLines((p) => [...p, { tag_id: "", pct: Math.round(untaggedPct) || 0 }]);
  const removeLine = (i) => setLines((p) => p.filter((_, idx) => idx !== i));
  const setLine = (i, patch) => setLines((p) => p.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (over) return toast.error("Allocations cannot exceed 100%");
    const allocations = lines
      .filter((l) => l.tag_id && Number(l.pct) > 0)
      .map((l) => ({ tag_id: l.tag_id, pct: Number(l.pct) }));
    setBusy(true);
    try {
      await api.post("/cost-tags/allocate", { collection: apiPath, record_id: row.id, allocations });
      toast.success(allocations.length ? "Cost tags saved" : "Cost tags cleared");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save allocation");
    } finally {
      setBusy(false);
    }
  };

  const tagMeta = (id) => tags.find((t) => t.id === id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="allocation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-4 w-4 text-brand" /> Tag to projects & cost centres
          </DialogTitle>
          <DialogDescription className="truncate">
            {row?.vendor || row?.source || row?.counterparty || row?.description || "Entry"} ·{" "}
            <span className="font-mono text-foreground">{formatMoney(base, "INR")}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : tags.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4" data-testid="alloc-no-tags">
            No cost tags yet. Create Projects and Cost Centres first on the{" "}
            <span className="text-foreground font-medium">Projects &amp; Cost Centres</span> page.
          </p>
        ) : (
          <div className="space-y-2">
            {lines.map((l, i) => {
              const meta = tagMeta(l.tag_id);
              const amt = (base * (Number(l.pct) || 0)) / 100;
              return (
                <div key={i} className="flex items-center gap-2" data-testid="alloc-line">
                  <Select value={l.tag_id} onValueChange={(v) => setLine(i, { tag_id: v })}>
                    <SelectTrigger className="flex-1 h-9" data-testid={`alloc-tag-select-${i}`}>
                      <SelectValue placeholder="Choose tag…" />
                    </SelectTrigger>
                    <SelectContent>
                      {tags.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          <span className="inline-flex items-center gap-1.5">
                            {t.type === "project" ? (
                              <FolderKanban className="h-3.5 w-3.5 text-brand" />
                            ) : (
                              <Wrench className="h-3.5 w-3.5 text-brand2" />
                            )}
                            {t.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="relative w-20">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={l.pct}
                      onChange={(e) => setLine(i, { pct: e.target.value })}
                      className="h-9 pr-5 text-right font-mono"
                      data-testid={`alloc-pct-${i}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                  </div>
                  <span className="w-24 text-right font-mono text-xs text-muted-foreground tabular-nums">
                    {formatMoney(amt, "INR")}
                  </span>
                  <button
                    onClick={() => removeLine(i)}
                    className="text-muted-foreground hover:text-danger p-1"
                    data-testid={`alloc-remove-${i}`}
                    aria-label="Remove"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}

            <Button
              variant="ghost"
              size="sm"
              onClick={addLine}
              className="gap-1.5 h-8 text-xs text-brand"
              data-testid="alloc-add-line"
            >
              <Plus className="h-3.5 w-3.5" /> Add split
            </Button>

            <div className="flex items-center justify-between pt-2 border-t border-border text-xs">
              <span className={over ? "text-danger font-medium" : "text-muted-foreground"} data-testid="alloc-allocated">
                Allocated {allocatedPct.toFixed(1)}%{over ? ` · over by ${(allocatedPct - 100).toFixed(1)}%` : ""}
              </span>
              <span className="text-muted-foreground" data-testid="alloc-untagged">
                Untagged {untaggedPct.toFixed(1)}% · {formatMoney((base * untaggedPct) / 100, "INR")}
              </span>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy || over || tags.length === 0} data-testid="alloc-save-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save tags"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
